import { useCallback, useEffect, useMemo, useState } from "react";
import useRefetchOnFocus from "../../../hooks/useRefetchOnFocus";
import { useNavigate } from "react-router-dom";
import { downloadDocumentBlob } from "../../../services/projectService";
import { triggerBlobDownload } from "utils/Common/fileDownload";
import useDebounce from "../../../hooks/useDebounce";
import Skeleton from "../../../Components/Common/Skeleton/Skeleton";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import {
  TYPE_CHIPS,
  ACTION_MESSAGES,
  URGENCY_BADGES,
  NOTIFICATION_ACTION_CONFIG,
} from "../notificationConstants";
import useNotifications, {
  ALERT_TYPE_TO_EVENT_TYPE,
} from "../hooks/useNotifications";
import type {
  Alert,
  FilterType,
  SettingsRow,
  UserRole,
} from "../types/notification.types";
import "../styles/Notification.scss";
import Input from "../../../Components/Common/Input/Input";
import SearchIcon from "../../../Assets/Img/SearchIcon";
import SearchableSelect from "../../../Components/Common/SearchableSelect/SearchableSelect";
import { getProjects } from "../../../services/projectService";
import type { Project } from "../../../hooks/useGetProjects";
import SettingsIcon from "../../../Assets/Img/SettingsIcon";
import {
  markAsRead,
  markAllAsRead,
  getNotificationSettings,
  updateNotificationSetting,
} from "../../../services/notificationService";
import { inviteLienWaiver } from "../../../services/drawService";
import { openMailTo, openTelTo } from "../../../utils/Common/contact";
import { useToast } from "Components/Common/Toast/ToastContext";
import NotificationSettings from "./NotificationSettings";
import Button from "Components/Common/Button/Button";
export const refreshNotificationCount = () => {
  window.dispatchEvent(new CustomEvent("notification-unread-count-changed"));
};
interface CSCAlertsProps {
  role?: UserRole;
}

export default function CSCAlerts({ role = "Admin" }: CSCAlertsProps) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState<UserRole>(role);
  const [activeType, setActiveType] = useState<FilterType>("all");
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 300);
  const [projectFilter, setProjectFilter] = useState("all");
  const [readFilter, setReadFilter] = useState<"all" | "unread" | "read">(
    "all",
  );
  const [page, setPage] = useState(1);
  const LIMIT = 10;
  const [settingsRows, setSettingsRows] = useState<SettingsRow[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [channelLoading, setChannelLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [markAllReadLoading, setMarkAllReadLoading] = useState(false);

  const [projectList, setProjectList] = useState<Project[]>([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectHasMore, setProjectHasMore] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const debouncedProjectSearch = useDebounce(projectSearch, 300);

  const fetchProjects = useCallback(
    async (page: number, q: string, append: boolean) => {
      setProjectLoading(true);
      const res = await (getProjects({
        page,
        limit: 20,
        q: q || undefined,
      }) as Promise<any>);
      const list: Project[] = (res?.data?.data ?? []).map((p: any) => ({
        id: String(p.id ?? p.projectId ?? ""),
        name: p.name ?? "",
        type: p.type ?? "",
        bank: p.stakeholders?.lenderBranch?.companyName ?? "",
        status: p.status ?? "",
        projectStage: p.projectStage ?? "",
        isGcAcknowledge: p.isGcAcknowledge ?? false,
        admin: {
          firstName: p.admin?.firstName ?? "",
          lastName: p.admin?.lastName ?? "",
        },
        contractAmount: p.contractValue?.amount ?? p.contractAmount ?? 0,
        currencySymbol: p.contractValue?.currency?.symbol ?? "$",
        gcName: p.gcName ?? "",
        lastDrawNumber: p.lastDrawNumber ?? null,
        lastDrawId: p.lastDrawId ?? "",
        stage: p.stage ?? p.projectStage ?? "",
        progress: p.progress ?? 0,
      }));
      const pg = res?.data?.pagination;
      setProjectHasMore(pg ? page < pg.totalPages : false);
      setProjectList((prev) => (append ? [...prev, ...list] : list));
      setProjectLoading(false);
    },
    [],
  );

  useEffect(() => {
    setProjectPage(1);
    fetchProjects(1, debouncedProjectSearch, false);
  }, [debouncedProjectSearch, fetchProjects]);

  const handleLoadMoreProjects = useCallback(() => {
    const next = projectPage + 1;
    setProjectPage(next);
    fetchProjects(next, debouncedProjectSearch, true);
  }, [projectPage, debouncedProjectSearch, fetchProjects]);

  const showToast = useToast();
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = storedUser?.role;
  const getLocalRoute = (action: string, alert: Alert): string | null => {
    const pid = alert.projectId;

    const projectBase = (tab: string): string => {
      if (!pid) return "/";
      switch (currentRole) {
        case "GC":
          return `/gc-project-detail/${pid}/${tab}`;
        case "Lender":
          return `/bank-project-detail/${pid}/${tab}`;
        case "Owner":
          return `/owner-project-detail/${pid}/${tab}`;
        default:
          return `/project-detail/${pid}/${tab}`;
      }
    };

    switch (action) {
      case "open_mobile_inspector":
        return "/project-inspection";
      case "assign_inspector":
        return "/inspector-assignment";
      case "view_user":
      case "view_user_profile":
        return alert?.email
          ? `/manage-users?email=${encodeURIComponent(alert.email)}`
          : "/manage-users";
    }

    if (!pid) return null;

    switch (action) {
      case "view_acknowledgement":
      case "view_acknowledgment":
      case "view_requirements_acknowledged":
      case "view_gc_requirements":
        return projectBase("requirements-acknowledged");
      case "review_report":
      case "review_precon_report":
      case "review_pre_con_report":
      case "view_pre_con_report":
      case "view_precon":
      case "review_precon":
      case "view_precon_report":
        return projectBase("precon");
      case "review_checklist":
      case "view_pre_con_checklist":
        return projectBase("precon-checklist");
      case "view_project":
      case "start_closeout":
        return projectBase("overview");
      case "view_draw":
      case "review_draw":
        return projectBase("draws");
      case "view_waiver":
      case "view_lien_waiver":
        return projectBase(currentRole === "GC" ? "waivers" : "draws");
      case "review_sov":
      case "view_sov":
        return projectBase("sov-version");
      case "submit_first_draw":
        return projectBase("draws");
      case "view_progress_report":
        return projectBase(currentRole === "Admin" ? "progress" : "reports");
      case "view_report":
      case "view_inspection":
      case "publish_report":
        return projectBase(currentRole === "Admin" ? "progress" : "reports");
      case "approve_change_order":
      case "reject_change_order":
      case "request_change_order_info":
      case "view_change_order":
        return projectBase("change-orders");
      default:
        return null;
    }
  };
  const isReadParam = readFilter === "all" ? undefined : readFilter === "read";
  const {
    alerts: apiAlerts,
    loading: apiLoading,
    pagination,
    typeCounts,
    unreadCount,
    refetch,
    refreshUnreadCount,
  } = useNotifications({
    search: debouncedSearch,
    isRead: isReadParam,
    projectId: projectFilter !== "all" ? projectFilter : undefined,
    viewAs: role === "Admin" ? currentRole.toLowerCase() : undefined,
    eventType:
      activeType !== "all" ? ALERT_TYPE_TO_EVENT_TYPE[activeType] : undefined,
    page,
    limit: LIMIT,
  });

  useEffect(() => {
    if (!apiLoading) {
      setAlerts(apiAlerts);
    }
  }, [apiAlerts, apiLoading]);

  useEffect(() => {
    setCurrentRole(role ?? "Admin");
  }, [role]);

  useRefetchOnFocus(refetch, refreshUnreadCount);

  useEffect(() => {
    if (!settingsOpen || settingsRows.length > 0) return;
    setSettingsLoading(true);
    (getNotificationSettings() as Promise<any>).then((res: any) => {
      const data: Record<string, any[]> = res?.data?.data ?? {};
      const rows: SettingsRow[] = (Object.values(data) as any[][])
        .flat()
        .map((item: any) => ({
          id: item.id,
          label: item.name,
          description: item.description,
          inApp: true,
          email: item.userPreferedChannels?.email ?? false,
          push: item.userPreferedChannels?.push ?? false,
          sms: item.userPreferedChannels?.sms ?? false,
          defaultChannels: item.defaultChannels,
        }));
      setSettingsRows(rows);
      setSettingsLoading(false);
    });
  }, [settingsOpen]);
  const roleAlerts = useMemo<Alert[]>(
    () => alerts.filter((a) => !a.hidden),
    [alerts],
  );

  const selectedProjectName = useMemo(
    () => projectList.find((p) => p.id === projectFilter)?.name ?? null,
    [projectList, projectFilter],
  );

  const filteredAlerts = useMemo<Alert[]>(
    () =>
      roleAlerts.filter((a) => {
        if (selectedProjectName && a.project !== selectedProjectName)
          return false;
        return true;
      }),
    [selectedProjectName, roleAlerts],
  );

  const groupedAlerts = useMemo(() => {
    const today: Alert[] = [];
    const yesterday: Alert[] = [];
    const week: Alert[] = [];
    const earlier: Alert[] = [];
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayMs = todayStart.getTime();
    filteredAlerts.forEach((alert) => {
      let daysAgo: number;
      if (alert.createdAt) {
        const d = new Date(alert.createdAt);
        d.setHours(0, 0, 0, 0);
        daysAgo = Math.round((todayMs - d.getTime()) / 86_400_000);
      } else {
        daysAgo = alert.ts <= 6 ? 0 : alert.ts <= 14 ? 1 : 8;
      }
      if (daysAgo === 0) today.push(alert);
      else if (daysAgo === 1) yesterday.push(alert);
      else if (daysAgo <= 7) week.push(alert);
      else earlier.push(alert);
    });
    return { today, yesterday, week, earlier };
  }, [filteredAlerts]);
  const clearFilters = (): void => {
    setActiveType("all");
    setSearchValue("");
    setProjectFilter("all");
    setReadFilter("all");
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, projectFilter, readFilter, activeType, currentRole]);

  const markRead = (id: string): void => {
    const alert = alerts.find((a) => a.id === id);
    if (!alert?.unread) return;
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, unread: false } : a)),
    );
    markAsRead(id).then(() => {
      refreshUnreadCount();
    });
  };

  const markAllRead = async (): Promise<void> => {
    setMarkAllReadLoading(true);
    try {
      await markAllAsRead();
      setAlerts((prev) => prev.map((a) => ({ ...a, unread: false })));
      refetch();
      refreshUnreadCount();
      showToast("All notifications marked as read");
    } finally {
      setMarkAllReadLoading(false);
    }
  };

  const handleAction = (id: string, label: string, action: string): void => {
    const config = NOTIFICATION_ACTION_CONFIG[action];
    const alert = alerts.find((a) => a.id === id);

    setAlerts((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a;
        const shouldHide = action === "dismiss" || action === "mark_resolved";
        return { ...a, unread: false, hidden: shouldHide };
      }),
    );

    if (
      action === "resend_waiver" &&
      alert?.projectId &&
      alert?.drawId &&
      alert?.lienWaiverId
    ) {
      (
        inviteLienWaiver(
          alert.projectId,
          alert.drawId,
          alert.lienWaiverId,
        ) as Promise<any>
      )
        .then(() => showToast(ACTION_MESSAGES["Resend Waiver"]))
        .catch(() => showToast("Failed to resend waiver"));
      return;
    }

    if (config?.type === "navigate") {
      const localRoute = alert ? getLocalRoute(action, alert) : null;
      if (localRoute) navigate(localRoute);
      return;
    }

    if (config?.type === "phone") {
      openTelTo(alert?.subPhone);
      return;
    }

    if (config?.type === "email") {
      openMailTo(alert?.bankEmail);
      return;
    }

    if (config?.type === "contact_inspector") {
      if (alert?.inspectorEmail) {
        openMailTo(alert.inspectorEmail);
      } else if (alert?.inspectorPhone) {
        openTelTo(alert.inspectorPhone);
      } else {
        showToast("No inspector contact information available");
      }
      return;
    }

    if (config?.type === "contact_gc") {
      if (alert?.gcEmail) {
        openMailTo(alert.gcEmail);
      } else if (alert?.gcPhone) {
        openTelTo(alert.gcPhone);
      } else {
        showToast("No GC contact information available");
      }
      return;
    }

    if (action === "download_report" && alert?.projectId && alert?.reportId) {
      showToast(ACTION_MESSAGES["Download PDF"]);
      downloadDocumentBlob(alert.projectId, alert.reportId, true).then(
        (blob) => {
          if (blob) triggerBlobDownload(blob, "report.pdf");
          else showToast("Failed to download report");
        },
      );
      return;
    }

    showToast(ACTION_MESSAGES[label] ?? label);
  };

  const toggleSettingChannel = async (
    id: string,
    channel: "inApp" | "email" | "sms",
  ): Promise<void> => {
    const key = `${id}-${channel}`;
    if (channelLoading[key]) return;

    const original = settingsRows.find((r) => r.id === id);
    if (!original) return;

    const toggled = {
      ...original,
      [channel]: !original[channel as keyof SettingsRow],
    };
    setSettingsRows((prev) => prev.map((r) => (r.id === id ? toggled : r)));
    setChannelLoading((prev) => ({ ...prev, [key]: true }));

    const res = await (updateNotificationSetting(id, {
      email: toggled.email,
      push: toggled.push ?? false,
      sms: toggled.sms ?? false,
    }) as Promise<any>);

    setChannelLoading((prev) => ({ ...prev, [key]: false }));

    if (res?.data?.success) {
      showToast(
        res.data.message ?? "Notification setting updated successfully",
      );
    } else {
      setSettingsRows((prev) => prev.map((r) => (r.id === id ? original : r)));
      showToast(
        res?.data?.message ??
          res?.error?.message ??
          "Failed to update preferences",
      );
    }
  };

  const renderAlert = (alert: Alert) => {
    const urgency = URGENCY_BADGES[alert.urgency];
    return (
      <div
        key={alert.id}
        className={`notif-item${alert.unread ? " unread" : ""}`}
        onClick={() => markRead(alert.id)}
      >
        {alert.unread && <div className="unread-dot" />}
        <div className={`notif-icon-wrap ${alert.ic}`}>{alert.icon}</div>
        <div className="notif-body">
          <div className="notif-top">
            <div className="notif-title">{alert.title}</div>
            <div className="notif-time">{alert.time}</div>
          </div>
          <div className="notif-detail">{alert.detail}</div>
          <div className="notif-meta">
            {alert.project && (
              <span className="notif-project">{alert.project}</span>
            )}
            {alert.tl && (
              <span className={`notif-type-tag ${alert.tc}`}>{alert.tl}</span>
            )}
            {urgency && (
              <span className={`urgency-badge ${urgency.className}`}>
                {urgency.label}
              </span>
            )}
          </div>
          <div className="notif-actions">
            {alert.actions
              .filter(
                (action) =>
                  !(
                    userRole === "admin" && action.action === "resend_waiver"
                  ) &&
                  ![
                    "approve_sov",
                    "request_sov_changes",
                    "approve_authorize_draw",
                    "request_precon_changes",
                    "schedule_inspection",
                  ].includes(action.action) &&
                  !(
                    action.action === "submit_first_draw" &&
                    currentRole !== "GC"
                  ) &&
                  !(
                    currentRole === "Inspector" &&
                    [
                      "view_draw",
                      "contact_inspector",
                      "review_draw",
                      "assign_inspector",
                    ].includes(action.action)
                  ),
              )
              .map((action) => (
                <Button
                  key={`${alert.id}-${action.label}`}
                  type="button"
                  className={`notif-btn${action.primary ? " primary" : ""}`}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleAction(alert.id, action.label, action.action);
                  }}
                >
                  {action.label}
                </Button>
              ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="csc-alerts">
      <div className="page">
        <div className="page-header">
          <div>
            <div className="page-title">Notification Center</div>
            {apiLoading ? (
              <Skeleton width={400} height={20} borderRadius={5} />
            ) : (
              <div className="page-sub">
                All alerts across every project, role, and event type —{" "}
                <strong>{unreadCount} unread</strong>
              </div>
            )}
          </div>
          <div className="header-actions">
            <Button
              type="button"
              className="btn btn-secondary"
              onClick={() => setSettingsOpen((prev) => !prev)}
            >
              <SettingsIcon />
              Notification Settings
            </Button>
            <Button
              type="button"
              className="btn btn-secondary"
              onClick={markAllRead}
              disabled={unreadCount === 0 || markAllReadLoading}
            >
              {markAllReadLoading && <span className="btn-spinner" />}
              Mark All Read
            </Button>
          </div>
        </div>

        {settingsOpen && (
          <NotificationSettings
            rows={settingsRows}
            loading={settingsLoading}
            channelLoading={channelLoading}
            onToggle={(id, channel) => toggleSettingChannel(id, channel)}
          />
        )}

        <div className="summary-row">
          {apiLoading
            ? Array.from({ length: TYPE_CHIPS.length }).map((_, i) => (
                <div
                  key={i}
                  className="summary-chip"
                  style={{ pointerEvents: "none" }}
                >
                  <Skeleton width={28} height={28} borderRadius={8} />
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <Skeleton width={56} height={11} borderRadius={4} />
                    <Skeleton width={20} height={14} borderRadius={4} />
                  </div>
                </div>
              ))
            : TYPE_CHIPS.map((chip) => (
                <Button
                  key={chip.key}
                  type="button"
                  className={`summary-chip${activeType === chip.key ? " active" : ""}`}
                  onClick={() => setActiveType(chip.key)}
                >
                  <div className="sc-icon" style={chip.iconStyle}>
                    {chip.icon}
                  </div>
                  <div>
                    <div className="sc-label">{chip.label}</div>
                    <div
                      className={`sc-count${chip.countClass ? ` ${chip.countClass}` : ""}`}
                    >
                      {typeCounts[chip.key] ?? 0}
                    </div>
                  </div>
                </Button>
              ))}
        </div>

        <div className="filter-bar">
          <div className="search-wrap">
            <Input
              className="search-input"
              type="text"
              placeholder="Search alerts..."
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
              icon={<SearchIcon />}
            />
          </div>
          <div
            className="filter-select"
            style={{ padding: 0, position: "relative" }}
          >
            <SearchableSelect<Project>
              value={projectFilter === "all" ? "" : projectFilter}
              placeholder="All projects"
              options={projectList}
              getOptionLabel={(p) => p.name}
              getOptionValue={(p) => p.id}
              apiSearch
              onSearch={setProjectSearch}
              onChange={(val) => setProjectFilter(val || "all")}
              onLoadMore={handleLoadMoreProjects}
              hasMore={projectHasMore}
              loading={projectLoading}
            />
          </div>
          <div
            className="filter-select"
            style={{ padding: 0, position: "relative" }}
          >
            <SearchableSelect
              value={readFilter === "all" ? "" : readFilter}
              options={[
                { value: "unread", label: "Unread only" },
                { value: "read", label: "Read only" },
              ]}
              onChange={(val) =>
                setReadFilter((val || "all") as "all" | "unread" | "read")
              }
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              placeholder="All statuses"
              disableSearch
            />
          </div>

          <Button type="button" className="btn-ghost" onClick={clearFilters}>
            Clear
          </Button>
        </div>

        <div className="notif-list">
          {apiLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="notif-item"
                style={{ pointerEvents: "none" }}
              >
                <Skeleton width={36} height={36} borderRadius={8} />
                <div className="notif-body">
                  <div className="notif-top">
                    <Skeleton width="55%" height={13} borderRadius={4} />
                    <Skeleton width={60} height={11} borderRadius={4} />
                  </div>
                  <Skeleton
                    width="80%"
                    height={11}
                    borderRadius={4}
                    style={{ marginTop: 6 }}
                  />
                  <Skeleton
                    width="50%"
                    height={11}
                    borderRadius={4}
                    style={{ marginTop: 4 }}
                  />
                  <div className="notif-actions" style={{ marginTop: 8 }}>
                    <Skeleton width={90} height={26} borderRadius={6} />
                    <Skeleton width={70} height={26} borderRadius={6} />
                  </div>
                </div>
              </div>
            ))
          ) : !filteredAlerts.length ? (
            <div className="empty-state">
              <div className="empty-icon">🔔</div>
              {searchValue ||
              projectFilter !== "all" ||
              readFilter !== "all" ||
              activeType !== "all" ? (
                <>
                  <div className="empty-title">No results found</div>
                  <div className="empty-sub">
                    No alerts match your current filters. Try adjusting or
                    clearing them.
                  </div>
                </>
              ) : (
                <>
                  <div className="empty-title">No alerts yet</div>
                  <div className="empty-sub">
                    You're all caught up. New alerts will appear here when
                    there's activity on your projects.
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {groupedAlerts.today.length > 0 && (
                <>
                  <div className="notif-group-header">Today</div>
                  {groupedAlerts.today.map(renderAlert)}
                </>
              )}
              {groupedAlerts.yesterday.length > 0 && (
                <>
                  <div className="notif-group-header">Yesterday</div>
                  {groupedAlerts.yesterday.map(renderAlert)}
                </>
              )}
              {groupedAlerts.week.length > 0 && (
                <>
                  <div className="notif-group-header">Past 7 Days</div>
                  {groupedAlerts.week.map(renderAlert)}
                </>
              )}
              {groupedAlerts.earlier.length > 0 && (
                <>
                  <div className="notif-group-header">Earlier</div>
                  {groupedAlerts.earlier.map(renderAlert)}
                </>
              )}
            </>
          )}
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={LIMIT}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
